import React, { useEffect, useState } from "react";
import Header from "../../components/Common";
import { toast } from "react-hot-toast";
import Loader from "../../components/Loader";
import { Popconfirm, Tooltip } from "antd";
import { FiSearch, FiPlus, FiEdit, FiTrash2, FiEye, FiLink } from "react-icons/fi";
import { FaEye, FaTrash, FaEdit, FaPlus, FaLink, FaImage, FaPlay } from "react-icons/fa";
import PaginatedItems from "../../components/paginationLayout";
import { usePageMetadata } from "../../hooks/usePageMetadata";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetCTAPopupsQuery, useDeleteCTAPopupMutation } from "../../store/slices/apiSlice";
import { CTA } from "../../store/slices/apiSlice";

const CTAManagement = () => {
    // Set page metadata
    usePageMetadata("ctaManagement");

    const navigate = useNavigate();
    const location = useLocation();
    const [ctaList, setCtaList] = useState<CTA[]>([]);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [itemOffset, setItemOffset] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("");

    // API calls
    const { data, isLoading, isError } = useGetCTAPopupsQuery();
    const [deleteCta, { isLoading: isDeleting }] = useDeleteCTAPopupMutation();

    // Determine the base path for navigation
    const basePath = location.pathname.startsWith('/admin') ? '/admin' : '/dashboard';

    useEffect(() => {
        if (data?.data) setCtaList(data.data as CTA[]);
    }, [data]);

    const handleDelete = async (id: number) => {
        try {
            const result = await deleteCta(id).unwrap();
            toast.success(result.message || "Ad deleted successfully");
            setCtaList(prev => prev.filter(item => item.popup_id !== id));
        } catch (error: any) {
            toast.error(error?.data?.message || "Failed to delete Ad");
        }
    };

    const handleEdit = (cta: CTA) => {
        navigate(`${basePath}/cta-management/edit/${cta.popup_id}`);
    };

    const handleAdd = () => {
        navigate(`${basePath}/cta-management/add`);
    };

    const filteredCtas = ctaList.filter((cta) => {
        const matchType =
            filterType === "image"
                ? cta.file_type === "image"
                : filterType === "video"
                    ? cta.file_type === "video"
                    : true;

        const matchSearch =
            cta.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cta.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cta.button_text.toLowerCase().includes(searchTerm.toLowerCase());

        return matchType && matchSearch;
    });

    useEffect(() => setItemOffset(0), [searchTerm, filterType]);

    const endOffset = itemOffset + itemsPerPage;
    const currentCtas = filteredCtas.slice(itemOffset, endOffset);
    const pageCount = Math.ceil(filteredCtas.length / itemsPerPage);

    const getTypeCount = (type: string) => {
        if (type === "") return ctaList.length;
        return ctaList.filter(cta => cta.file_type === type).length;
    };

    const getTypeBadge = (type: string) => {
        const typeConfig: Record<string, { color: string; text: string; icon: JSX.Element }> = {
            image: { color: "bg-blue-100 text-blue-800", text: "Image", icon: <FaImage /> },
            video: { color: "bg-purple-100 text-purple-800", text: "Video", icon: <FaPlay /> }
        };

        const config = typeConfig[type] || typeConfig.image;
        return (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color} flex items-center gap-1`}>
                {config.icon}
                {config.text}
            </span>
        );
    };

    const renderTargetCountries = (cta: CTA) => {
        const countries = cta.target_countries;
        const isGlobal = !countries || countries.length === 0;
        if (isGlobal) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    🌍 Global
                </span>
            );
        }
        if (countries.length <= 3) {
            return (
                <div className="flex flex-wrap gap-1">
                    {countries.map(code => (
                        <span key={code} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-mono">{code}</span>
                    ))}
                </div>
            );
        }
        return (
            <div className="flex flex-wrap gap-1 items-center">
                {countries.slice(0, 2).map(code => (
                    <span key={code} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-mono">{code}</span>
                ))}
                <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">+{countries.length - 2} more</span>
            </div>
        );
    };

    const renderThumbnail = (cta: CTA) => {
        // Priority: thumbnail_image > file (if image type)
        const imageUrl = cta.thumbnail_image || (cta.file_type === 'image' ? cta.file : null);

        if (!imageUrl) {
            return (
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    {cta.file_type === 'video' ? (
                        <FaPlay className="text-gray-400 text-lg" />
                    ) : (
                        <FaImage className="text-gray-400 text-lg" />
                    )}
                </div>
            );
        }

        return (
            <div className="relative group">
                <img
                    src={imageUrl}
                    alt={`${cta.title} thumbnail`}
                    className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                    onError={(e) => {
                        // Fallback to placeholder if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                    }}
                />
                <div className="hidden w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    {cta.file_type === 'video' ? (
                        <FaPlay className="text-gray-400 text-lg" />
                    ) : (
                        <FaImage className="text-gray-400 text-lg" />
                    )}
                </div>

                {/* Hover preview */}
                <div className="absolute bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-2">
                        <img
                            src={imageUrl}
                            alt={`${cta.title} preview`}
                            className="w-32 h-32 object-cover rounded"
                        />
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Ads</h3>
                    <p className="text-gray-600">Please try again later</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <FaLink className="text-purple-600" />
                            App Ads Slider
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Manage app ads slider and promotional content
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search ads..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                        </div>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                            <option value="">All Types</option>
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                        </select>
                        <button
                            onClick={handleAdd}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                            <FiPlus size={16} />
                            Add Ad
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Ads</p>
                            <p className="text-2xl font-bold text-gray-900">{ctaList.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                            <FaLink className="text-purple-600 text-xl" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Image Ads</p>
                            <p className="text-2xl font-bold text-blue-600">{getTypeCount("image")}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <FaImage className="text-blue-600 text-xl" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Video Ads</p>
                            <p className="text-2xl font-bold text-purple-600">{getTypeCount("video")}</p>
                        </div>
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                            <FaPlay className="text-purple-600 text-xl" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">This Month</p>
                            <p className="text-2xl font-bold text-green-600">
                                {ctaList.filter(cta => {
                                    const ctaDate = new Date(cta.created_at);
                                    const now = new Date();
                                    return ctaDate.getMonth() === now.getMonth() &&
                                        ctaDate.getFullYear() === now.getFullYear();
                                }).length}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                            <FaLink className="text-green-600 text-xl" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Ads Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Thumbnail
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Ad Details
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Target
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Link
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Created Date
                                </th>
                                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {currentCtas.length > 0 ? (
                                currentCtas.map((cta, idx) => (
                                    <tr key={cta.popup_id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {renderThumbnail(cta)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                                                    <FaLink className="text-purple-600" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {cta.title}
                                                    </div>
                                                    <div className="text-xs text-gray-500 max-w-xs truncate">
                                                        {cta.description}
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Button: {cta.button_text}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getTypeBadge(cta.file_type)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {renderTargetCountries(cta)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 max-w-xs truncate">
                                                {cta.href}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {cta.created_at
                                                    ? dayjs(cta.created_at).format("MMM DD, YYYY")
                                                    : "N/A"}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {cta.created_at
                                                    ? dayjs(cta.created_at).format("HH:mm")
                                                    : ""}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-3">
                                                <Tooltip title="Edit Ad">
                                                    <button
                                                        onClick={() => handleEdit(cta)}
                                                        className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                                                    >
                                                        <FaEdit size={16} />
                                                    </button>
                                                </Tooltip>
                                                <Popconfirm
                                                    title="Are you sure to delete this ad?"
                                                    description="This action cannot be undone."
                                                    okText="Yes, Delete"
                                                    cancelText="Cancel"
                                                    onConfirm={() => handleDelete(cta.popup_id)}
                                                    disabled={isDeleting}
                                                >
                                                    <Tooltip title="Delete Ad">
                                                        <button
                                                            className="text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                                                            disabled={isDeleting}
                                                        >
                                                            <FaTrash size={16} />
                                                        </button>
                                                    </Tooltip>
                                                </Popconfirm>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center">
                                        <div className="text-center">
                                            <div className="text-gray-400 text-6xl mb-4">🔗</div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Ads Found</h3>
                                            <p className="text-gray-600">No ads match your current filters</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {pageCount >= 2 && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <PaginatedItems
                        pageCount={pageCount}
                        onPageChange={(e) => setItemOffset(e.selected * itemsPerPage)}
                        onItemsPerPageChange={(val) => {
                            setItemsPerPage(val);
                            setItemOffset(0);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default CTAManagement; 